import { z } from "zod";

const dateString = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be YYYY-MM-DD");

const hours = z
  .number()
  .min(0.5, "Minimum 0.5 hours (30 min)")
  .max(24, "Hours cannot exceed 24")
  .refine((v) => Math.round(v * 2) === v * 2, { message: "Must be in 30-minute increments" });

const notes = z.string().max(500, "Notes cannot exceed 500 characters").nullable().optional();

const entryType = z.enum(["billable", "overhead"]);

const baseFields = {
  entryDate: dateString,
  type: entryType,
  woNumber: z.string().nullable().optional(),
  category: z.string().nullable().optional(),
  subtask: z.string().nullable().optional(),
  overheadCode: z.string().nullable().optional(),
  overheadSub: z.string().nullable().optional(),
  overheadCategoryId: z.string().uuid().nullable().optional(),
  overheadSubcategoryId: z.string().uuid().nullable().optional(),
  hours,
  notes,
};

function notFutureRefinement(data: { entryDate?: string }, ctx: z.RefinementCtx) {
  if (!data.entryDate) return;
  const today = new Date();
  const todayISO = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  if (data.entryDate > todayISO) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["entryDate"],
      message: "Date cannot be in the future",
    });
  }
}

function typeBranchRefinement(
  data: {
    type?: "billable" | "overhead";
    woNumber?: string | null;
    category?: string | null;
    subtask?: string | null;
    overheadCode?: string | null;
  },
  ctx: z.RefinementCtx,
) {
  if (data.type === "billable") {
    if (!data.woNumber) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["woNumber"],
        message: "Please select a work order",
      });
    }
    if (!data.category) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["category"],
        message: "Please select an activity category",
      });
    }
    if (!data.subtask) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["subtask"],
        message: "Please select a subtask",
      });
    }
  } else if (data.type === "overhead") {
    if (!data.overheadCode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["overheadCode"],
        message: "Please select an overhead category",
      });
    }
  }
}

export const timeEntryWriteSchema = z
  .object(baseFields)
  .superRefine(notFutureRefinement)
  .superRefine(typeBranchRefinement);

export const timeEntryEditSchema = z
  .object({
    ...baseFields,
    hours: hours.optional(),
    type: entryType.optional(),
    entryDate: dateString.optional(),
    status: z.enum(["draft", "submitted", "approved", "flagged"]).optional(),
  })
  .superRefine(notFutureRefinement);

export const timeEntryFlaggedEditSchema = z.object({
  hours,
  category: z.string().min(1, "Please select an activity category"),
  subtask: z.string().min(1, "Please select a subtask"),
  notes,
});
